import { Component, ElementRef, ViewChild } from '@angular/core';
import { CodeReaderBaseComponent } from '@features/code-readers/code-reader-base/code-reader-base.component';
import jsQR, { QRCode } from 'jsqr';

@Component({
  templateUrl: './qr-reader.component.html',
  imports: [CodeReaderBaseComponent],
})
export class QrReaderComponent {
  @ViewChild('qrReader') qrReader!: ElementRef<HTMLVideoElement>;

  private responsePromise?: Promise<QRCode>;
  private rejectPromise?: (reason?: string) => void;
  private stream?: MediaStream;

  public startVideoStream(): Promise<QRCode> {
    this.responsePromise = new Promise(async (resolve, reject) => {
      this.rejectPromise = reject;

      try {
        this.stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        const video = this.qrReader.nativeElement;
        video.srcObject = this.stream;
        video.setAttribute('playsinline', 'true');

        await video.play();
        requestAnimationFrame(() => this.tick().then(resolve).catch(reject));
      } catch (error) {
        console.error(error);
        reject(error);
      }
    });

    // Cleanup when the promise resolves
    this.responsePromise.then(
      () => this.cleanup(),
      () => this.cleanup(),
    );

    return this.responsePromise;
  }

  protected close() {
    if (this.responsePromise) {
      this.rejectPromise?.('Operation canceled by user');
      this.cleanup();
    }
  }

  private tick(): Promise<QRCode> {
    return new Promise((resolve, reject) => {
      const video = this.qrReader.nativeElement;

      const canvasElement = document.createElement('canvas');
      canvasElement.width = video.videoWidth;
      canvasElement.height = video.videoHeight;

      const canvasContext = canvasElement.getContext('2d');
      if (!canvasContext) {
        requestAnimationFrame(() => this.tick().then(resolve).catch(reject));
        return;
      }

      canvasContext.drawImage(video, 0, 0, canvasElement.width, canvasElement.height);

      const imageData = canvasContext.getImageData(0, 0, canvasElement.width, canvasElement.height);
      if (!imageData) {
        requestAnimationFrame(() => this.tick().then(resolve).catch(reject));
        return;
      }

      const code = jsQR(imageData.data, imageData.width, imageData.height);
      if (code && code.data.length > 0) {
        resolve(code);
      } else {
        requestAnimationFrame(() => this.tick().then(resolve).catch(reject));
      }
    });
  }

  private cleanup() {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = undefined;
    }
    if (this.qrReader.nativeElement.srcObject) {
      this.qrReader.nativeElement.srcObject = null;
    }
    this.rejectPromise = undefined;
  }
}
